import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectUserId, useUpdateInvitationMutation } from "fond/api";
import { ACCOUNT_CONTEXT, SEARCH_KEY } from "fond/constants";
import { useCanIPlanner } from "fond/hooks/useCanIPlanner";
import { useNoAccountRedirect } from "fond/hooks/useNoAccountRedirect";
import { useUpdateDocTitle } from "fond/hooks/useUpdateDocTitle";
import SearchResults from "fond/projects/GlobalSearch/SearchResults";
import MainWorkspaceContent from "fond/projects/MainWorkspaceContent";
import { Navigation } from "fond/projects/Navigation";
import { useQueryParams } from "fond/utils/hooks";
import { getItem, setItem } from "fond/utils/localStorage";
import { PageContent } from "fond/widgets";
import { NavButton } from "fond/widgets/Link/NavButton";

/**
 * A top level component used for displaying project.
 */
const MainWorkspace: React.FC = () => {
  useNoAccountRedirect();
  useUpdateDocTitle("Home");

  const [updateInvitation] = useUpdateInvitationMutation();
  const navigate = useNavigate();
  const searchQuery = useQueryParams(SEARCH_KEY);
  const { pathname } = useLocation();
  const { state: locationState } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const userId = useSelector(selectUserId);
  const { canBuy, hasSub } = useCanIPlanner();

  useEffect(() => {
    if (!locationState?.invitationId) return;

    const handleInviteAcceptance = async () => {
      const { invitationId } = locationState;
      try {
        const data = await updateInvitation({ invitation: { ID: invitationId, InvitationStatus: "accepted" }, userType: "existing" }).unwrap();
        if (userId && data.User.ID === userId) {
          enqueueSnackbar("Switching account...");
          const currentAccountContext = getItem(ACCOUNT_CONTEXT);
          setItem(ACCOUNT_CONTEXT, currentAccountContext ? { ...currentAccountContext, [userId]: data.AccountID } : { [userId]: data.AccountID });
        } else {
          Sentry.captureMessage("Invited user doesn't match current logged in user");
          navigate("/invitations/invalid");
        }
        navigate(0);
      } catch (error) {
        Sentry.captureException(error);
        navigate("/invitations/invalid", { state: { multipleAccountsError: true } });
      }
    };

    handleInviteAcceptance();
  }, [enqueueSnackbar, locationState, locationState?.invitationId, navigate, updateInvitation, userId]);

  // workaround to redirect / to /workspace
  useEffect(() => {
    if (pathname === "/") navigate("/workspace/");
  }, [navigate, pathname]);

  /**
   * Global search results page
   */
  if (searchQuery) {
    return <SearchResults />;
  }

  return (
    <PageContent
      title="Main Workspace"
      action={
        canBuy && (
          <NavButton
            onClick={() => analytics.track("workspace.upgrade.button.clicked")}
            to={hasSub ? "/subscription/manage" : "/subscription/get-started"}
          >
            Upgrade to planner
          </NavButton>
        )
      }
      mainContent={<MainWorkspaceContent />}
      navigation={<Navigation />}
      testId="filter-workspace"
    />
  );
};

export default MainWorkspace;
